/* eslint-disable no-secrets/no-secrets */
/* eslint-disable pii/no-phone-number */
import { AddToCartContext, AddToCartEvent } from './events/add-to-cart.model';
import { ClickContext, ClickEvent, ZenitClickAction } from './events/click.model';
import { ImpressionsHitContext, ImpressionsHitEvent } from './events/impressions-hit.model';
import { PageHitContext, PageHitEvent } from './events/page-hit-event.model';
import { PurchaseConfirmedContext, PurchaseConfirmedEvent } from './events/purchase-confirmed.model';

import { CartItem } from '@api/cart/cart.models';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { EmpathySearcherOrigin } from '@core/domain/types/empathy-searcher-origin.type';
import { PageSection } from '@core/domain/types/page-sections.types';
import { ProductPublicModels } from '@features/products/product';
import { ProductsArray } from '@features/products/productsArray/domain/models/productsArray.models';
import { SidebarCustomComponents } from '@features/sidebar';
import { GridElement } from '@pages/category/modules/category-grid/public_api';
// import { QueryResult, SearchType } from '@features/search';
import { QueryResult, SearchType } from '@pages/search';
import { AnalyticsClickSourceID, ItemTypes } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ViewItemProduct } from '@shared/analytics/COMMON/services/product-view/view-item.service';
import {
    SearchAssistantContext,
    SearchAssistantEvent,
} from '@shared/analytics/ZENIT/models/events/search-assistant.model';
import { GridType } from '@shared/models/grid-type.model';
import { ProductViewPlace } from '@shared/product/models';
import { RelatedsPlace } from '@shared/product/related-products/infrastructure/state/related-products.state';

export enum ZenitEventName {
    AddToCart = 'AddToCart',
    Click = 'Click',
    ImpressionsHit = 'ImpressionsHit',
    PageHit = 'PageHit',
    PurchaseConfirmed = 'PurchaseConfirmed',
    SearchAssistant = 'SearchAssistant',
}

export type ZenitEvent =
    AddToCartEvent |
    ClickEvent |
    ImpressionsHitEvent |
    PageHitEvent |
    PurchaseConfirmedEvent |
    SearchAssistantEvent;

export interface ZenitEventValues {
    brandId?: 16;
    cartId?: number;
    cartItems?: CartItem[];
    categoryId?: string;
    categoryUrl?: string;
    currency?: CurrencyInfo;
    currentUrl?: string;
    displayLanguage?: string;
    filteredProductsIds?: number[];
    isSelectedAddress?: boolean;
    item?: CartItem | ProductPublicModels.PublicProduct;
    items?: ItemTypes[];
    page?: PageSection;
    pageGridElements?: GridElement[];
    productPageProduct?: ProductPublicModels.PublicProduct;
    productsArray?: ProductsArray;
    queryResults?: QueryResult[];
    recommenderSize?: string;
    relatedProductsPlaces?: Record<ProductViewPlace, RelatedsPlace>;
    searchRequestFilters?: string;
    searchRequestOffset?: number;
    searchRequestQuery?: string;
    searchType?: SearchType;
    section?: string;
    selectedViewPickup?: 'list' | 'map';
    sidebarComponent?: SidebarCustomComponents;
    sidebarData?: Record<string, any>;
    storeId?: string;
    wishListItems?: ProductPublicModels.PublicProduct[];
    zoomLevel?: GridType;
}

export interface ZenitEventData {
    action?: ZenitClickAction;
    items?: ItemTypes[] | ViewItemProduct[];
    origin?: EmpathySearcherOrigin | SearchType;
    place?: ProductViewPlace;
    provider?: string;
    quantity?: number;
    relatedsSectionId?: string;
    responseCode?: number;
    clickSourceID?: AnalyticsClickSourceID;
    responseTime?: number;
    searchPass?: string;
    searchTerm?: string;
    totalHits?: string | number;
    totalResults?: number;
}

// Parámetros existentes de los eventos de Zenit
export type ZenitEventParam =
    keyof AddToCartContext
    | keyof ClickContext
    | keyof ImpressionsHitContext
    | keyof PageHitContext
    | keyof PurchaseConfirmedContext
    | keyof SearchAssistantContext;

export enum ZenitPageType {
    BUY_AGAIN = 'BUY_AGAIN',
    CATEGORY_VIEW = 'CATEGORY_VIEW',
    CHAT = 'CHAT',
    CHECKOUT_ADD_PERSONAL_DETAILS = 'CHECKOUT_ADD_PERSONAL_DETAILS',
    CHECKOUT_DROP_POINT_DETAIL = 'CHECKOUT_DROP_POINT_DETAIL',
    CHECKOUT_DROP_POINT_LIST = 'CHECKOUT_DROP_POINT_LIST',
    CHECKOUT_DROP_POINT_MAP = 'CHECKOUT_DROP_POINT_MAP',
    CHECKOUT_EXPRESS_STORE_DETAIL = 'CHECKOUT_EXPRESS_STORE_DETAIL',
    CHECKOUT_EXPRESS_STORE_LIST = 'CHECKOUT_EXPRESS_STORE_LIST',
    CHECKOUT_HOME_ADDRESS_LIST = 'CHECKOUT_HOME_ADDRESS_LIST',
    CHECKOUT_MODIFY_PERSONAL_DETAILS = 'CHECKOUT_MODIFY_PERSONAL_DETAILS',
    CHECKOUT_ORDER_REVIEW = 'CHECKOUT_ORDER_REVIEW',
    CHECKOUT_PAYMENT_METHOD = 'CHECKOUT_PAYMENT_METHOD',
    CHECKOUT_REPAYMENT = 'CHECKOUT_REPAYMENT',
    CHECKOUT_SHIPPING_METHOD = 'CHECKOUT_SHIPPING_METHOD',
    CHECKOUT_STORE_COLLECTION_POINT_MAP = 'CHECKOUT_STORE_COLLECTION_POINT_MAP',
    CHECKOUT_STORE_DETAIL = 'CHECKOUT_STORE_DETAIL',
    CHECKOUT_STORE_LIST = 'CHECKOUT_STORE_LIST',
    CHECKOUT_TYPE_DELIVERY_METHOD = 'CHECKOUT_TYPE_DELIVERY_METHOD',
    COMING_SOON_CONFIRMATION = 'COMING_SOON_CONFIRMATION',
    CONTACT = 'CONTACT',
    E_GIFTCARD = 'E-GIFTCARD',
    E_GIFTCARD_ACTIVATION = 'E_GIFTCARD_ACTIVATION',
    E_GIFTCARD_BALANCE = 'E_GIFTCARD_BALANCE',
    GIFT_OPTIONS = 'GIFT_OPTIONS',
    GIFTCARD = 'GIFTCARD',
    HELP_CENTER = 'HELP_CENTER',
    HOME = 'HOME',
    HOME_LOYALTY = 'HOME_LOYALTY',
    LOOKBOOK = 'LOOKBOOK',
    MIX_MATCH_DETAIL = 'MIX_MATCH_DETAIL',
    MIX_MATCH_LIST = 'MIX_MATCH_LIST',
    MULTI_WISHLIST = 'MULTI_WISHLIST',
    MY_ACCOUNT_ACCESS_DETAILS = 'MY_ACCOUNT_ACCESS_DETAILS',
    MY_ACCOUNT_ADDRESSES = 'MY_ACCOUNT_ADDRESSES',
    MY_ACCOUNT_APPOINTMENTS = 'MY_ACCOUNT_APPOINTMENTS',
    MY_ACCOUNT_LOYALTY_BENEFITS = 'MY_ACCOUNT_LOYALTY_BENEFITS',
    MY_ACCOUNT_LOYALTY_HISTORY = 'MY_ACCOUNT_LOYALTY_HISTORY',
    MY_ACCOUNT_LOYALTY_HOW_IT_WORKS = 'MY_ACCOUNT_LOYALTY_HOW_IT_WORKS',
    MY_ACCOUNT_MENU = 'MY_ACCOUNT_MENU',
    MY_ACCOUNT_NEWSLETTER = 'MY_ACCOUNT_NEWSLETTER',
    MY_ACCOUNT_ORDER_RETURNS_EXCHANGES_ITEMS = 'MY_ACCOUNT_ORDER_RETURNS_EXCHANGES_ITEMS',
    MY_ACCOUNT_ORDERS_LIST_IN_STORE = 'MY_ACCOUNT_ORDERS_LIST_IN_STORE',
    MY_ACCOUNT_ORDERS_LIST_ONLINE = 'MY_ACCOUNT_ORDERS_LIST_ONLINE',
    MY_ACCOUNT_PERSONAL_DETAILS = 'MY_ACCOUNT_PERSONAL_DETAILS',
    MY_ACCOUNT_RETURNS_LIST = 'MY_ACCOUNT_RETURNS_LIST',
    MY_ACCOUNT_WALLET = 'MY_ACCOUNT_WALLET',
    NEWSLETTER_SUBSCRIBE_VIEW = 'NEWSLETTER_SUBSCRIBE_VIEW',
    NEWSLETTER_UNSUBSCRIBE_VIEW = 'NEWSLETTER_UNSUBSCRIBE_VIEW',
    ORDER_TRACK = 'ORDER_TRACK',
    PAGE_NOT_FOUND = 'PAGE_NOT_FOUND',
    PASSWORD_RECOVERY = 'PASSWORD_RECOVERY',
    PASSWORD_RECOVERY_RESET = 'PASSWORD_RECOVERY_RESET',
    PRODUCT_BUNDLE_DETAILS = 'PRODUCT_BUNDLE_DETAILS',
    PRODUCT_DETAILS = 'PRODUCT_DETAILS',
    PRODUCT_LIST = 'PRODUCT_LIST',
    PRODUCT_SEARCH_RESULTS = 'PRODUCT_SEARCH_RESULTS',
    PRODUCT_SEARCH_START = 'PRODUCT_SEARCH_START',
    PRODUCT_SET_DETAILS = 'PRODUCT_SET_DETAILS',
    PURCHASE_ORDER_END = 'PURCHASE_ORDER_END',
    RETURNABLE_PRODUCTS_SELECTOR = 'RETURNABLE_PRODUCTS_SELECTOR',
    SAVE_FOR_LATER = 'SAVE_FOR_LATER',
    SEARCH_REQUEST = 'SEARCH_REQUEST',
    SHOP_LOCATOR = 'SHOP_LOCATOR',
    SHOPPING_CART = 'SHOPPING_CART',
    SHOPPING_CART_EMPTY = 'SHOPPING_CART_EMPTY',
    STORE_DETAIL = 'STORE_DETAIL',
    SURPRISE_ME = 'SURPRISE_ME',
    UNKNOWN = 'UNKNOWN',
    USER_LOGON = 'USER_LOGON',
    USER_LOYALTY_CHECK = 'USER_LOYALTY_CHECK',
    USER_LOYALTY_TICKET = 'USER_LOYALTY_TICKET',
    USER_REGISTRATION = 'USER_REGISTRATION',
    USER_REGISTRATION_CONFIRMATION = 'USER_REGISTRATION_CONFIRMATION',
    VIRTUAL_TRY_ON = 'VIRTUAL_TRY_ON',
    WORLDWIDE = 'WORLDWIDE',
}

/** Actualmente equivalente a la propiedad "section" debido a que solo existen los "universe" WOMAN y MAN */
export enum ZenitUniverse {
    'MAN' = 'MAN',
    'WOMAN' = 'WOMAN',
    'KID' = 'KID',
    'Z3D' = 'Z3D',
    'BEAUTY' = 'BEAUTY',
    'HOME' = 'HOME',
    'MASSIMO DUTTI' = 'MASSIMO DUTTI',
    'ZARA PRE-OWNED' = 'ZARA PRE-OWNED',
    'SAPATOS' = 'SAPATOS',
    'BOLSAS' = 'BOLSAS',
}

export enum ZenitList {
    alternativeColours = 'alternative-colours',
    alternativeResults = 'alternative-results',
    bundle = 'bundle',
    buyAgain = 'buy-again',
    buyLater = 'buy-later',
    crossCustomPdp = 'cross-custom-pdp',
    crossCustomToast = 'cross-custom-toast',
    crossCustomWishlist = 'cross-custom-wishlist',
    crossRecommendation = 'cross-recommendation', // Zara Home
    crossRecommendationPdp = 'cross-recommendation-pdp',
    crossSellingCart = 'cross-selling-cart',
    crossSellingPdp = 'cross-selling-pdp',
    crossSellingToast = 'cross-selling-toast',
    crossSimilarBackSoon = 'cross-similar-back-soon',
    crossSimilarComingSoon = 'cross-similar-coming-soon',
    crossSimilarCustomPdp = 'cross-similar-custom-pdp',
    crossSimilarGrid = 'cross-similar-grid',
    crossSimilarPdp = 'cross-similar-pdp',
    crossSimilarSearchResults = 'cross-similar-search-results',
    crossViewedRecently = 'cross-viewed-recently',
    edited = 'edited',
    grid = 'grid',
    matchWith = 'match-with',
    mayAlsoLike = 'may-also-like',
    noList = 'no-list',
    noListUndefined = 'no-list-undefined',
    noResultsFallback = 'no-results-fallback',
    saveForLater = 'save-for-later',
    searchAlternativeColours = 'search-alternative-colours',
    searchAlternativeResults = 'search-alternative-results',
    searchCrossCustom = 'search-cross-custom',
    searchNoResultsFallback = 'search-no-results-fallback',
    searchResults = 'search-results',
    searchSimilarQuery = 'search-similar-query',
    searchSuggestedProduct = 'search-suggested-product',
    shoppingCart = 'shopping-cart',
    sizeList = 'size-list',
    sizeRecommender = 'size-recommender',
    stradishoppers = 'stradishoppers',
    suggestedProduct = 'suggested-product',
    suggestedSearchResults = 'suggested-search-results',
    wishlist = 'wishlist',
}

export enum Section {
    MEN = 'MAN',
    WOMEN = 'WOMAN',
    KID = 'KID',
    UNKNOWN = 'UNKNOWN',
}

export enum ZenitZoomLevel {
    ZOOM1 = 'ZOOM1',
    ZOOM2 = 'ZOOM2',
    ZOOM3 = 'ZOOM3',
}

export interface ZenitItem {
    /* Total amount charged for this product in local currency, store currency */
    amount: number;
    /* Code of the brand that is selling this product [Bershka: 4, Lefties: 8, MassimoDutti: 11, Oysho: 7, Pull&Bear: 2, Stradivarius: 6, Uterqüe: 18, Zara: 1, 16, ZaraHome: 14] */
    articleBrandCode?: number;
    /* Unitary price of the product */
    price: number;
    /* SKU including campaign. This is MOCACOTA. Example: 0807324080005-I2021 */
    sku: string;
    /* Number of units purchased */
    units: number;
    section?: Section;
    brandId?: number;
    /** Code that identifies the view/image that is presented to the user when visualizing certain product(s) The value corresponds to different identifiers (p, e0, a1, ult, ...) associated to a different type of image/view of the product */
    viewOrigin: string;
}

export interface ZenitPersonalizationParam {
    /** Objective of the personalization strategy. Examples: othersyoumaylike-cvr-divnone */
    objective?: string;
    /** Name of the recomendation provider. Examples: Google, YODA */
    provider: string;
    /** Name of the method or strategy followed in the recommendation. Examples: FBT, OYML, Similars */
    strategy?: string;
    /** Version of the strategy. Examples: 1.0, 2.0 */
    version?: string;
    /** This value is optional. Contains extra information about the personalization that could be gathered. */
    extra?: Record<string, any>;
}
